<template>
  <section class="admin-content" id="marketplace-admin-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white flex-wrap" style="padding: 32px 0; gap: 12px;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3"><i class="fe fe-shield" style="font-size: 22px;"></i></div>
              <div>
                <h4 class="mb-0 font-weight-bold">Marketplace Vendors</h4>
                <small style="opacity: .8;">Approve vendors who sell to health facilities</small>
              </div>
            </div>
            <router-link to="/marketplace" class="btn btn-light btn-sm"><i class="fe fe-external-link mr-1"></i>Open marketplace</router-link>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="access && !access.agency_admin && !access.platform_admin" class="alert alert-warning">
            Only agency admins and marketplace administrators can manage vendors.
          </div>

          <ul class="nav nav-pills mb-3" role="tablist" v-if="access">
            <li class="nav-item" v-if="access.agency_admin">
              <button type="button" class="nav-link" :class="{ active: tab === 'agency' }" role="tab" :aria-selected="tab === 'agency'" @click="tab = 'agency'">
                <i class="fe fe-home mr-1"></i>Applications to my agency
              </button>
            </li>
            <li class="nav-item" v-if="access.platform_admin">
              <button type="button" class="nav-link" :class="{ active: tab === 'platform' }" role="tab" :aria-selected="tab === 'platform'" @click="tab = 'platform'">
                <i class="fe fe-globe mr-1"></i>All vendors (platform)
              </button>
            </li>
            <li class="nav-item" v-if="access.platform_admin">
              <button type="button" class="nav-link" :class="{ active: tab === 'tags' }" role="tab" :aria-selected="tab === 'tags'" @click="tab = 'tags'">
                <i class="fe fe-tag mr-1"></i>Product tags
              </button>
            </li>
          </ul>

          <!-- Platform admins override which products are offered for assessment gaps (app/Hefrias/CLAUDE.md) -->
          <div v-if="tab === 'tags'" class="card shadow-sm table-card p-4">
            <p class="small text-muted">
              Vendors tag their products to hefrias standard items; facilities are offered tagged products when an assessment finds the item missing.
              Search a listed product to add tags or block it for an item.
            </p>
            <form class="d-flex mb-3" style="gap: 8px;" @submit.prevent="searchProducts">
              <label for="ma-pq" class="sr-only">Search products</label>
              <input id="ma-pq" v-model="productQ" type="search" class="form-control form-control-sm styled-input" style="max-width: 300px;" placeholder="Product name, brand, SKU…" />
              <button type="submit" class="btn btn-sm btn-outline-primary">Search</button>
            </form>
            <div v-for="p in productResults" :key="p.id" class="tag-product">
              <button type="button" class="btn btn-link p-0 text-left font-weight-bold" :aria-expanded="tagProductId === p.id"
                @click="tagProductId = tagProductId === p.id ? null : p.id">
                {{ p.name }}
              </button>
              <span class="small text-muted"> · {{ p.vendor ? p.vendor.name : "" }} · ₦{{ Number(p.unit_price).toLocaleString() }}</span>
              <StandardItemTags v-if="tagProductId === p.id" :product-id="p.id" admin class="mt-2" />
            </div>
            <div v-if="productSearched && !productResults.length" class="small text-muted">No listed product matches.</div>
          </div>

          <div class="d-flex align-items-center mb-3" style="gap: 8px;" v-if="tab !== 'tags' && access && (access.agency_admin || access.platform_admin)">
            <label for="ma-status" class="small font-weight-bold mb-0">Status</label>
            <select id="ma-status" v-model="status" class="form-control form-control-sm styled-input" style="max-width: 180px;" @change="load">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option v-if="tab === 'platform'" value="suspended">Suspended</option>
            </select>
            <template v-if="tab === 'platform'">
              <label for="ma-q" class="sr-only">Search vendors</label>
              <input id="ma-q" v-model="q" type="search" class="form-control form-control-sm styled-input" style="max-width: 240px;"
                placeholder="Name, email, RC number…" @keyup.enter="load" />
            </template>
          </div>

          <div class="card shadow-sm table-card" v-if="tab !== 'tags' && access && (access.agency_admin || access.platform_admin)">
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead style="background:#f8fafc;">
                  <tr>
                    <th class="table-th">Vendor</th>
                    <th class="table-th">Contact</th>
                    <th class="table-th">Products</th>
                    <th class="table-th">Status</th>
                    <th class="table-th"><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.key" class="table-row">
                    <td class="px-4 py-3">
                      <div class="font-weight-bold" style="font-size:.9rem;">{{ row.vendor.name }}</div>
                      <div class="small text-muted">
                        <span v-if="row.vendor.rc_number">RC {{ row.vendor.rc_number }} · </span>{{ row.vendor.state || "—" }}
                      </div>
                      <div class="small text-muted" v-if="row.vendor.description" style="max-width: 360px;">{{ row.vendor.description }}</div>
                    </td>
                    <td class="py-3 small">{{ row.vendor.email }}<br />{{ row.vendor.phone_number }}</td>
                    <td class="py-3">
                      <router-link v-if="row.vendor.status === 'approved'" :to="{ path: '/marketplace', query: { vendor: row.vendor.id } }">
                        {{ row.products }}
                      </router-link>
                      <span v-else>{{ row.products }}</span>
                    </td>
                    <td class="py-3">
                      <span class="status-pill" :class="'status-pill--' + row.status">{{ row.status }}</span>
                      <div class="small text-muted" v-if="row.reason">{{ row.reason }}</div>
                      <div class="small text-muted" v-if="tab === 'agency' && row.vendor.status !== 'approved'">
                        Platform: {{ row.vendor.status }}
                      </div>
                    </td>
                    <td class="py-3 pr-4 text-right text-nowrap">
                      <button v-if="row.status !== 'approved'" type="button" class="btn btn-sm btn-success mr-1" @click="decide(row, 'approved')">Approve</button>
                      <button v-if="row.status === 'pending' || (tab === 'agency' && row.status === 'approved')" type="button"
                        class="btn btn-sm btn-outline-danger mr-1" @click="decide(row, 'rejected')">Reject</button>
                      <button v-if="tab === 'platform' && row.status === 'approved'" type="button" class="btn btn-sm btn-outline-danger"
                        @click="decide(row, 'suspended')">Suspend</button>
                    </td>
                  </tr>
                  <tr v-if="!rows.length">
                    <td colspan="5" class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size:2rem;opacity:.3"></i>
                      <p class="mt-2 mb-0">Nothing here.</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import StandardItemTags from "../assessments/StandardItemTags.vue";

export default {
  components: { Navbar, StandardItemTags },
  data() {
    return {
      access: null, tab: "agency", status: "pending", q: "", rows: [],
      productQ: "", productResults: [], productSearched: false, tagProductId: null,
    };
  },
  watch: {
    tab(value) {
      if (value !== "tags") this.load();
    },
  },
  created() {
    this.axios.get("/api/v1/auth/marketplace/access").then((res) => {
      this.access = res.data;
      this.tab = res.data.agency_admin ? "agency" : "platform";
      if (res.data.agency_admin || res.data.platform_admin) this.load();
    });
  },
  methods: {
    notify(message) {
      this.$breadstick.notify(message, { position: "top-right" });
    },
    load() {
      const params = { per_page: 200 };
      if (this.status) params.status = this.status;
      if (this.tab === "platform") {
        if (this.q) params.q = this.q;
        this.axios.get("/api/v1/auth/marketplace/admin/vendors", { params }).then((res) => {
          this.rows = (res.data.data || []).map((v) => ({
            key: "v" + v.id, id: v.id, vendor: v, status: v.status, reason: v.status_reason, products: v.products_count,
          }));
        });
      } else {
        this.axios.get("/api/v1/auth/marketplace/vendor-applications", { params }).then((res) => {
          this.rows = (res.data.data || []).map((a) => ({
            key: "a" + a.id, id: a.id, vendor: a.vendor || {}, status: a.status, reason: a.reason,
            products: a.vendor ? a.vendor.products_count : 0,
          }));
        });
      }
    },
    searchProducts() {
      this.axios.get("/api/v1/marketplace/products", { params: { q: this.productQ || undefined, per_page: 30, sort: "name" } }).then((res) => {
        this.productResults = res.data.data || [];
        this.productSearched = true;
        this.tagProductId = null;
      });
    },
    decide(row, status) {
      let reason = null;
      if (status !== "approved") {
        reason = window.prompt(`Reason for ${status === "suspended" ? "suspending" : "rejecting"} ${row.vendor.name} (shown to the vendor):`, "");
        if (reason === null) return;
      }
      const request = this.tab === "platform"
        ? this.axios.post(`/api/v1/auth/marketplace/admin/vendors/${row.id}/status`, { status, reason })
        : this.axios.post(`/api/v1/auth/marketplace/vendor-applications/${row.id}`, { status, reason });
      request
        .then((res) => {
          this.notify(res.data.message || "Saved");
          this.load();
        })
        .catch((err) => this.notify((err.response && err.response.data && err.response.data.message) || "Failed"));
    },
  },
};
</script>

<style scoped>
.tag-product { border-top: 1px solid #f1f5f9; padding: 10px 0; }
.page-header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.styled-input { border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: .88rem; }
.table-card { border-radius: 12px; border: none; }
.table-th { font-size: .73rem; text-transform: uppercase; letter-spacing: .04em; color: #64748b; font-weight: 600; border-bottom: 2px solid #e2e8f0; padding: 12px 16px; }
.table-row:hover { background: #f8fafc; }
.status-pill { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: .75rem; font-weight: 600; text-transform: capitalize; background: #e2e8f0; color: #334155; }
.status-pill--approved { background: #d1fae5; color: #065f46; }
.status-pill--pending { background: #fef3c7; color: #92400e; }
.status-pill--rejected, .status-pill--suspended { background: #fee2e2; color: #991b1b; }
.nav-pills .nav-link { border: none; background: #fff; margin-right: 6px; color: #334155; border-radius: 8px; font-size: .88rem; }
.nav-pills .nav-link.active { background: #1e3a5f; color: #fff; }
</style>
